export const generateUniqueUrl = (name: string): string => {
    const sanitizedName = name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

    const timestamp = Date.now().toString(36);
    const randomSuffix = Math.random().toString(36).substring(2, 8);
    return `${sanitizedName}-${timestamp}-${randomSuffix}`;
};

export const generateUniqueUrlWithCheck = async (name: string, supabase: any): Promise<string> => {
    let baseUrl = generateUniqueUrl(name);
    let finalUrl = baseUrl;
    let counter = 1;
    const maxAttempts = 10; // Prevent infinite loops

    // Check if URL exists and generate a new one if needed
    while (counter <= maxAttempts) {
        try {
            const { data, error } = await supabase
                .from('assistants')
                .select('unique_url')
                .eq('unique_url', finalUrl);

            if (error) {
                // Error occurred, use the current URL
                console.warn('Error checking URL uniqueness:', error);
                break;
            }

            if (!data || data.length === 0) {
                // No rows returned, URL is unique
                break;
            }

            // URL exists, generate a new one
            finalUrl = `${baseUrl}-${counter}`;
            counter++;
        } catch (err) {
            console.warn('Exception while checking URL uniqueness:', err);
            break;
        }
    }

    // If we've exhausted all attempts, add a random string
    if (counter > maxAttempts) {
        const randomSuffix = Math.random().toString(36).substring(2, 8);
        finalUrl = `${baseUrl}-${randomSuffix}`;
    }

    return finalUrl;
};

export const formatDate = (date: string | Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

export const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
};

export const sanitizeUrlHandle = (input: string): string => {
    return input
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
};

export const checkUrlHandleUniqueness = async (urlHandle: string, supabase: any, excludeUserId?: string): Promise<boolean> => {
    try {
        let query = supabase
            .from('assistants')
            .select('unique_url')
            .eq('unique_url', urlHandle);

        if (excludeUserId) {
            query = query.neq('user_id', excludeUserId);
        }

        const { data, error } = await query;

        if (error) {
            console.warn('Error checking URL handle uniqueness:', error);
            return false; // Assume not unique if there's an error
        }

        return !data || data.length === 0;
    } catch (err) {
        console.warn('Exception while checking URL handle uniqueness:', err);
        return false; // Assume not unique if there's an error
    }
};

export const getCurrentUrlPrefix = (): string => {
    // Get the current URL and extract the protocol and hostname
    const currentUrl = window.location.origin;

    // For development, this will be something like "http://localhost:3000"
    // For production, this will be the actual domain
    return currentUrl + '/';
};
